import { useEffect, useState } from "react";
import axios from "axios";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const setApi = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/sets`;
const gamesApi = `${process.env.REACT_APP_BACKEND_URL}/api/catalog/public/games`;

const initialForm = {
  game: "Pokémon",
  card_name: "",
  set_name: "",
  set_code: "",
  catalog_set_id: "",
  condition: "Near Mint",
  language: "English",
  printing: "",
  quantity: 1,
  unit_cost: "0",
  purchase_date: "",
  purchase_source: "",
  seller: "",
  notes: "",
};

export default function IntakeItemDialog({
  batch,
  open,
  onOpenChange,
  onCreate,
}) {
  const [form, setForm] = useState(initialForm);
  const [sets, setSets] = useState([]);
  const [games, setGames] = useState(["Pokémon"]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    axios
      .get(setApi, {
        params: { game: form.game, search: form.set_name, limit: 30 },
        withCredentials: true,
      })
      .then((response) => setSets(response.data.sets))
      .catch(() => setSets([]));
  }, [open, form.game, form.set_name]);

  useEffect(() => {
    if (!open) {
      return;
    }
    axios
      .get(gamesApi, { withCredentials: true })
      .then((response) => {
        setGames(response.data.games);
      })
      .catch(() => setGames(["Pokémon"]));
  }, [open]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const chooseSet = (setName) => {
    const selected = sets.find((set) => set.set_name === setName);
    update("set_name", setName);
    if (selected) {
      update("set_code", selected.set_code || "");
      update("catalog_set_id", selected.catalog_set_id || "");
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!batch) {
      return;
    }
    setSaving(true);
    try {
      await onCreate(batch.batch_id, {
        ...form,
        quantity: Number(form.quantity),
        purchase_date: form.purchase_date || null,
        set_code: form.set_code || null,
        set_name: form.set_name || null,
      });
      setForm(initialForm);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="workspace-dialog workspace-dialog-wide"
        data-testid="intake-item-dialog"
      >
        <DialogHeader>
          <DialogTitle>Add card to intake</DialogTitle>
          <DialogDescription>
            {batch
              ? `Adding to ${batch.name}`
              : "Select a batch before adding cards."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="workspace-form workspace-form-grid"
          onSubmit={submit}
          data-testid="intake-item-form"
        >
          <label>
            <span>Game</span>
            <input
              list="intake-game-options"
              value={form.game}
              onChange={(event) => update("game", event.target.value)}
              data-testid="intake-game-input"
            />
            <datalist id="intake-game-options">
              {games.map((game) => (
                <option key={game} value={game} />
              ))}
            </datalist>
          </label>
          <label>
            <span>Card name</span>
            <input
              value={form.card_name}
              onChange={(event) => update("card_name", event.target.value)}
              required
              data-testid="intake-card-name-input"
            />
          </label>
          <label>
            <span>Frozen v1 set</span>
            <input
              list="intake-set-options"
              value={form.set_name}
              onChange={(event) => chooseSet(event.target.value)}
              placeholder="Search name or code"
              data-testid="intake-set-search-input"
            />
            <datalist id="intake-set-options">
              {sets.map((set) => (
                <option
                  key={set.set_id}
                  value={set.set_name}
                  label={`${set.display_name || set.set_name} (${set.set_code || "No code"})`}
                />
              ))}
            </datalist>
          </label>
          <label>
            <span>Set code</span>
            <input
              value={form.set_code}
              onChange={(event) => update("set_code", event.target.value)}
              data-testid="intake-set-code-input"
            />
          </label>
          <label>
            <span>Quantity</span>
            <input
              type="number"
              min="1"
              value={form.quantity}
              onChange={(event) => update("quantity", event.target.value)}
              required
              data-testid="intake-quantity-input"
            />
          </label>
          <label>
            <span>Condition</span>
            <select
              value={form.condition}
              onChange={(event) => update("condition", event.target.value)}
              data-testid="intake-condition-select"
            >
              {[
                "Near Mint",
                "Lightly Played",
                "Moderately Played",
                "Heavily Played",
                "Damaged",
                "Sealed",
                "Ungraded",
                "Other",
              ].map((condition) => (
                <option key={condition}>{condition}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Unit cost</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.unit_cost}
              onChange={(event) => update("unit_cost", event.target.value)}
              data-testid="intake-unit-cost-input"
            />
          </label>
          <label>
            <span>Purchase date</span>
            <input
              type="date"
              value={form.purchase_date}
              onChange={(event) => update("purchase_date", event.target.value)}
              data-testid="intake-purchase-date-input"
            />
          </label>
          <label>
            <span>Source</span>
            <input
              value={form.purchase_source}
              onChange={(event) =>
                update("purchase_source", event.target.value)
              }
              data-testid="intake-source-input"
            />
          </label>
          <label className="workspace-form-wide">
            <span>Notes</span>
            <textarea
              value={form.notes}
              onChange={(event) => update("notes", event.target.value)}
              data-testid="intake-notes-input"
            />
          </label>
          <button
            className="admin-primary-button workspace-form-wide"
            disabled={saving || !batch}
            type="submit"
            data-testid="save-intake-item-button"
          >
            {saving ? "Adding…" : "Add to intake"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
